import { useQuery } from "@tanstack/react-query";
import { Suspense, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { portalFetch } from "../../api/http";
import { isMigrated, portalHref } from "../../shell/routes";
import { BheliLoader } from "../../ui/brand";
import { lazyScreen } from "../../ui/lazyScreen";
import { useInternalLinks } from "../../team/DraftGuard";
import { loadTeamContext, TeamApi } from "../../team/teamApi";
import { prefetchZones } from "../../team/TeamPickers";
import { teamLabels, teamPrefixes, teamSharedKeys, text, type TeamUi } from "../../team/teamText";
import { FeedbackScope, MenuScope, More, TeamContext } from "../../team/TeamUi";
import { GroupRoleEditor } from "../../team/GroupRoleEditor";

// Chat › Equipo (Equipo.razor + OperatingTeamWorkspace) y Horarios y disponibilidad de Chat › Configuración
// (Configuracion.razor + OperatingSettingsWorkspace, secciones Horarios y Feriados y cierres). Quién puede abrir cada una lo
// decide el servidor (/api/portal/team/context); sin permiso, el estado de página con el motivo y la salida (Q11).

// Cada sección se carga aparte; ya cargada se dibuja sin la espera de Suspense (ui/lazyScreen).
const MembersWorkspace = lazyScreen(
  () => import("../../team/MembersWorkspace").then((m) => m.MembersWorkspace),
  /^\/portal\/chat\/equipo(\/miembros)?$/,
);
const MemberDetail = lazyScreen(
  () => import("../../team/MemberDetail").then((m) => m.MemberDetail),
  /^\/portal\/chat\/equipo\/miembros\/\d+$/,
);
const EffectiveAccess = lazyScreen(
  () => import("../../team/EffectiveAccess").then((m) => m.EffectiveAccess),
  /^\/portal\/chat\/equipo\/miembros\/\d+\/acceso$/,
);
const GroupsWorkspace = lazyScreen(
  () => import("../../team/GroupsWorkspace").then((m) => m.GroupsWorkspace),
  /^\/portal\/chat\/equipo\/grupos(\/\d+)?$/,
);
const SchedulesWorkspace = lazyScreen(
  () => import("../../team/SchedulesWorkspace").then((m) => m.SchedulesWorkspace),
  /^\/portal\/chat\/configuracion\/horarios(\/\d+)?$/,
);
const ClosuresWorkspace = lazyScreen(
  () => import("../../team/ClosuresWorkspace").then((m) => m.ClosuresWorkspace),
  /^\/portal\/chat\/configuracion\/cierres$/,
);

/** Rutas de Equipo y de Horarios y disponibilidad que navegan sin recargar. */
export const teamRoutes = [
  /^\/portal\/chat\/equipo(\/miembros(\/\d+(\/acceso)?)?|\/grupos(\/\d+)?)?$/i,
  /^\/portal\/chat\/configuracion\/(horarios(\/\d+)?|cierres)$/i,
];
const isTeamPath = (path: string) => teamRoutes.some((route) => route.test(path.replace(/\/+$/, "")));

const theme = (): "light" | "dark" =>
  document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";

function useTeamUi(locale: string) {
  const context = useQuery({ queryKey: ["team-context"], queryFn: loadTeamContext, staleTime: 0, gcTime: 0 });
  const texts = useQuery({
    queryKey: ["texts", locale, "team"],
    queryFn: async () =>
      teamLabels(
        await portalFetch<Record<string, string>>(
          `/api/portal/texts/${locale}?prefixes=${encodeURIComponent([...teamPrefixes, ...teamSharedKeys].join(","))}`,
        ),
      ),
  });
  const organization = context.data?.organizationId;
  useEffect(() => {
    if (organization) prefetchZones(new TeamApi(organization));
  }, [organization]);
  const ui = useMemo<TeamUi | null>(
    () =>
      context.data && texts.data
        ? {
            labels: texts.data,
            locale,
            timeZone: context.data.timeZone,
            userId: context.data.userId,
            organizationId: context.data.organizationId,
            theme: theme(),
          }
        : null,
    [context.data, texts.data, locale],
  );
  return { context: context.data, ui };
}

function Denied({
  ui,
  title,
  line,
  action,
  href,
}: {
  ui: TeamUi;
  title: string;
  line: string;
  action: string;
  href: string;
}) {
  return (
    <section className="tm">
      <div className="tm-denied" role="status" data-part="sin-permiso">
        <strong>{text(ui, title)}</strong>
        <span>{text(ui, line)}</span>
        <a className="tm-btn secondary" href={href}>
          {text(ui, action)}
        </a>
      </div>
    </section>
  );
}

/**
 * El carril del botón Chats (C27) que en el portal actual calcula chat-app.js (positionPortalFloaters): con una barra fija
 * de botones en el celular ([data-fixed-actions]), el alto que se reserva abajo y el de la barra. Mientras el Dock no se
 * migre (T24) lo hace esta pantalla con la misma fórmula; si el Dock está, lo deja a él.
 */
export function usePortalReserve() {
  useEffect(() => {
    const root = document.documentElement;
    let frame = 0;
    const place = () => {
      frame = 0;
      if ((window as { __bheliDockReady?: boolean }).__bheliDockReady) return;
      const mobile = innerWidth <= 767;
      const focused = document.activeElement?.matches(
        "textarea,input:not([type=button]):not([type=checkbox]):not([type=radio]):not([type=file])",
      );
      const viewport = window.visualViewport;
      const keyboard = mobile && (focused || (viewport && innerHeight - viewport.height - viewport.offsetTop > 80));
      const actions = [...document.querySelectorAll<HTMLElement>("[data-fixed-actions]")].find(
        (element) => element.getClientRects().length && getComputedStyle(element).position === "fixed",
      );
      const bar = mobile && actions ? Math.max(0, innerHeight - actions.getBoundingClientRect().top) : 0;
      const launcherBottom = bar ? bar + 12 : 18;
      const reserve = keyboard ? bar + 12 : launcherBottom + 56 + 12;
      if (root.style.getPropertyValue("--portal-bottom-reserve") !== `${reserve}px`)
        root.style.setProperty("--portal-bottom-reserve", `${reserve}px`);
      if (root.style.getPropertyValue("--portal-actionbar-height") !== `${bar}px`)
        root.style.setProperty("--portal-actionbar-height", `${bar}px`);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(place);
    };
    const observer = new MutationObserver(schedule);
    const main = document.querySelector("main#main");
    if (main) observer.observe(main, { childList: true, subtree: true });
    addEventListener("resize", schedule);
    document.addEventListener("focusin", schedule);
    document.addEventListener("focusout", schedule);
    place();
    return () => {
      observer.disconnect();
      removeEventListener("resize", schedule);
      document.removeEventListener("focusin", schedule);
      document.removeEventListener("focusout", schedule);
      if (frame) cancelAnimationFrame(frame);
      if (!(window as { __bheliDockReady?: boolean }).__bheliDockReady) {
        root.style.removeProperty("--portal-bottom-reserve");
        root.style.removeProperty("--portal-actionbar-height");
      }
    };
  }, []);
}

/** Rodea la vista: el cargador de arranque se oculta al aparecer .cfg1-live (portal.css), como el portal actual. */
function Activation({ ui, children }: { ui: TeamUi; children: React.ReactNode }) {
  const root = useRef<HTMLElement>(null);
  useInternalLinks(root, isTeamPath);
  usePortalReserve();
  return (
    <section className="cfg1-activation" ref={root}>
      <div className="cfg1-startup">
        <BheliLoader label={text(ui, "BRAND_LOADING")} theme={ui.theme} />
      </div>
      {children}
    </section>
  );
}

/** /portal/chat/equipo/… : Miembros (lista, Configurar agente y acceso efectivo) y Grupos. */
export function TeamPage({ locale }: { locale: string }) {
  const { context, ui } = useTeamUi(locale);
  const location = useLocation();
  const params = useParams();
  const memberId = Number(params.memberId ?? 0);
  const groupId = Number(params.groupId ?? 0);
  const path = location.pathname.toLowerCase();
  const section = path.endsWith("/acceso") ? "access" : path.includes("/grupos") ? "groups" : "members";
  useEffect(() => {
    if (ui) document.title = `${text(ui, "CFG1_TEAM")} · Bheli`;
  }, [ui]);
  if (!context || !ui) return null;
  if (!context.team)
    return memberId > 0 ? (
      <Denied
        ui={ui}
        title="CFG1_B_DENIED_TITLE"
        line="CFG1_B_DENIED_LINE"
        action="CFG1_B_BACK_MEMBERS"
        href="/portal/chat/equipo/miembros"
      />
    ) : (
      <Denied
        ui={ui}
        title="CFG1_R1_DENIED_TITLE"
        line="CFG1_R1_DENIED_HINT"
        action="CFG1_R1_GO_CONVERSATIONS"
        href={portalHref("/portal/chat")}
      />
    );
  const tabs = [
    { section: "members", label: "CFG1_MEMBERS", path: "miembros" },
    { section: "groups", label: "CFG1_GROUPS", path: "grupos" },
  ];
  return (
    <TeamContext.Provider value={ui}>
      <Activation ui={ui}>
        <div className="tm" data-floating-lane="scroll">
          <nav className="tm-tabs cfg1-live" aria-label={text(ui, "CFG1_TEAM")} data-part="pestanas">
            {tabs.map((tab) => (
              <a
                key={tab.section}
                href={`/portal/chat/equipo/${tab.path}`}
                aria-current={
                  section === tab.section || (section === "access" && tab.section === "members") ? "page" : undefined
                }
              >
                {text(ui, tab.label)}
              </a>
            ))}
          </nav>
          <FeedbackScope undoLabel={text(ui, "CFG1_R1_UNDO")}>
            {/* Grupos es una sola pantalla para la lista y el grupo elegido; Miembros, una por persona. */}
            <MenuScope key={section === "groups" ? "groups" : `${section}:${memberId}`}>
              <Suspense fallback={<More />}>
                {section === "groups" ? (
                  <GroupsWorkspace groupId={groupId} />
                ) : section === "access" ? (
                  <EffectiveAccess memberId={memberId} />
                ) : memberId > 0 ? (
                  <MemberDetail memberId={memberId} />
                ) : (
                  <MembersWorkspace />
                )}
              </Suspense>
            </MenuScope>
          </FeedbackScope>
        </div>
      </Activation>
    </TeamContext.Provider>
  );
}

/**
 * /portal/chat/configuracion/horarios y /cierres (Gestionar bandejas): dos entradas de Configuración, cada una con su
 * encabezado ← a Configuración, como Bandejas, sin pestañas.
 */
export function AvailabilityPage({ locale }: { locale: string }) {
  const { context, ui } = useTeamUi(locale);
  const location = useLocation();
  const params = useParams();
  const scheduleId = Number(params.scheduleId ?? 0);
  const section = location.pathname.toLowerCase().includes("/cierres") ? "closures" : "schedules";
  useEffect(() => {
    if (ui) document.title = `${text(ui, section === "closures" ? "CFG1_CLOSURES" : "CFG1_SCHEDULES")} · Bheli`;
  }, [ui, section]);
  if (!context || !ui) return null;
  if (!context.manageInboxes)
    return (
      <Denied
        ui={ui}
        title="CFG1_R1_DENIED_TITLE"
        line="CFG1_R1_DENIED_HINT"
        action="CFG1_R1_GO_CONVERSATIONS"
        href={portalHref("/portal/chat")}
      />
    );
  return (
    <TeamContext.Provider value={ui}>
      <Activation ui={ui}>
        <div className="tm cfg1-live" data-floating-lane="scroll">
          <FeedbackScope undoLabel={text(ui, "CFG1_R1_UNDO")}>
            <MenuScope key={location.pathname}>
              <Suspense fallback={<More />}>
                {section === "schedules" ? <SchedulesWorkspace scheduleId={scheduleId} /> : <ClosuresWorkspace />}
              </Suspense>
            </MenuScope>
          </FeedbackScope>
        </div>
      </Activation>
    </TeamContext.Provider>
  );
}

/**
 * El detalle de una función de grupo dentro de Roles y permisos de Chat › Configuración (OperatingChatRolesWorkspace dibuja
 * OperatingRolesWorkspace con el contexto de Equipo). Lo usa RolesPage; el marco de la página (.tm) lo pone RolesPage.
 */
export function GroupRoleHost({ locale, roleId, duplicate }: { locale: string; roleId: number; duplicate: boolean }) {
  const { ui } = useTeamUi(locale);
  // Sin contexto todavía no hay textos (More los necesita): no se dibuja nada hasta tenerlos.
  if (!ui) return null;
  return (
    <TeamContext.Provider value={ui}>
      <FeedbackScope undoLabel={text(ui, "CFG1_R1_UNDO")}>
        <MenuScope key={`role:${roleId}:${duplicate}`}>
          <GroupRoleEditor roleId={roleId} duplicate={duplicate} />
        </MenuScope>
      </FeedbackScope>
    </TeamContext.Provider>
  );
}

/**
 * EquipoAnterior.razor: las direcciones viejas de Horarios, Feriados y cierres y Roles (y «Horarios» de Configuración de la
 * empresa) llevan al lugar nuevo de Chat › Configuración con su búsqueda y su página, reemplazando la entrada del historial.
 */
export function TeamRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  useEffect(() => {
    const path = location.pathname.toLowerCase();
    const roleId = Number(params.roleId ?? 0),
      scheduleId = Number(params.scheduleId ?? 0);
    const target = path.includes("/roles")
      ? roleId > 0
        ? `/portal/chat/configuracion/roles/${roleId}`
        : "/portal/chat/configuracion/roles"
      : path.includes("/cierres")
        ? "/portal/chat/configuracion/cierres"
        : scheduleId > 0
          ? `/portal/chat/configuracion/horarios/${scheduleId}`
          : "/portal/chat/configuracion/horarios";
    // Roles y permisos ya está migrado (T29): se navega sin recargar, como a Equipo.
    if (isTeamPath(target) || isMigrated(target)) navigate(target + location.search, { replace: true });
    else window.location.replace(portalHref(target) + location.search);
  }, [location.pathname, location.search, navigate, params.roleId, params.scheduleId]);
  return null;
}
